import { useEffect, useState, useCallback } from "react";
import { FiTrendingUp, FiUsers, FiDollarSign, FiCalendar, FiChevronLeft, FiChevronRight, FiAward, FiPieChart, FiBarChart2, FiGlobe, FiX, FiUser, FiCheckCircle } from "react-icons/fi";
import axios from "../api/axios";

/* ═══════════════════════════════════════════
   VIBRANT NEUROMORPHISM STYLES — Consistent with All Dashboards
   Blue · Green · Purple · Amber palette
═══════════════════════════════════════════ */
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800;900&display=swap');

  .lvl-root {
    font-family: 'Montserrat', sans-serif;
    background: linear-gradient(135deg, #e0e5ec 0%, #d1d6df 100%);
    min-height: 100%;
    padding: 6px 5px;
  }
  .lvl-root *, .lvl-root *::before, .lvl-root *::after { box-sizing: border-box; }
  
  .lvl-root ::-webkit-scrollbar { width: 6px; height: 6px; }
  .lvl-root ::-webkit-scrollbar-track { 
    background: #d1d6df;
    border-radius: 99px;
    box-shadow: inset 0 1px 3px rgba(0,0,0,.1);
  }
  .lvl-root ::-webkit-scrollbar-thumb { 
    background: linear-gradient(135deg, #2563eb, #8b5cf6);
    border-radius: 99px;
    box-shadow: inset 0 1px 2px rgba(255,255,255,.3), inset 0 -1px 2px rgba(0,0,0,.1);
  }

  .lvl-page {
    max-width: 1280px;
    margin: 0 auto;
    display: flex; flex-direction: column; gap: 18px;
  }
  @media (max-width: 600px) { .lvl-page { gap: 14px; } }

  /* ══════════════════════════════
     HERO SECTION — Vibrant Gradient
  ══════════════════════════════ */
  .lvl-hero {
    background: linear-gradient(135deg, #0a1628 0%, #1e3a5f 50%, #2563eb 100%);
    border-radius: 7px;
    padding: 16px 18px;
    display: flex; align-items: center; justify-content: space-between;
    gap: 10px; flex-wrap: wrap;
    position: relative;
    box-shadow: 8px 8px 16px #8f95a0, -8px -8px 16px #ffffff;
    border: 1px solid rgba(255,255,255,0.18);
  }
  .lvl-hero::before {
    content: '';
    position: absolute;
    top: -14%;
    right: -4%;
    width: 180px;
    height: 180px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(59,130,246,.18) 0%, transparent 70%);
    pointer-events: none;
  }
  .lvl-hero::after {
    content: '';
    position: absolute;
    bottom: -14%;
    left: -4%;
    width: 150px;
    height: 150px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(139,92,246,.12) 0%, transparent 70%);
    pointer-events: none;
  }
  .lvl-hero-left { position: relative; z-index: 1; }
  .lvl-hero-badge {
    display: inline-flex; align-items: center; gap: 6px;
    background: rgba(255,255,255,0.12);
    backdrop-filter: blur(8px);
    border: 1px solid rgba(255,255,255,0.25);
    border-radius: 99px; padding: 4px 10px;
    font-size: 9px; font-weight: 800; color: #fbbf24;
    letter-spacing: .08em; text-transform: uppercase; margin-bottom: 8px;
  }
  .lvl-badge-dot {
    width: 5px; height: 5px; border-radius: 50%; background: #fbbf24;
    animation: lvl-pulse 2s infinite;
    box-shadow: 0 0 4px rgba(251,191,36,0.6);
  }
  @keyframes lvl-pulse { 0%,100%{opacity:1; transform:scale(1)} 50%{opacity:.5; transform:scale(1.2)} }
  .lvl-hero-title {
    font-size: clamp(18px, 4vw, 24px); font-weight: 900;
    color: #ffffff; line-height: 1.2; margin-bottom: 4px;
    text-shadow: 2px 2px 4px rgba(0,0,0,0.2);
  }
  .lvl-hero-sub { font-size: 11px; font-weight: 600; color: rgba(255,255,255,0.85); }

  .lvl-hero-stats {
    display: flex; gap: 8px; position: relative; z-index: 1; flex-wrap: wrap;
  }
  .lvl-hero-stat {
    background: rgba(255,255,255,0.1);
    backdrop-filter: blur(8px);
    border: 1px solid rgba(255,255,255,0.2);
    border-radius: 16px; padding: 10px 14px; text-align: center;
    min-width: 100px;
    transition: all 0.2s;
    box-shadow: 3px 3px 8px rgba(0,0,0,.1), -1px -1px 6px rgba(255,255,255,.1);
  }
  .lvl-hero-stat:hover {
    transform: translateY(-2px);
    background: rgba(255,255,255,0.2);
  }
  .lvl-hero-stat-val {
    font-size: clamp(16px, 2vw, 20px); font-weight: 900;
    background: linear-gradient(135deg, #fbbf24, #f59e0b);
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
  .lvl-hero-stat-label {
    font-size: 9px; font-weight: 800; color: rgba(255,255,255,0.8);
    text-transform: uppercase; letter-spacing: .08em; margin-top: 4px;
  }

  /* ══════════════════════════════
     MONTH FILTER BAR — Neumorphism
  ══════════════════════════════ */
  .lvl-filter-bar {
    background: linear-gradient(145deg, #e8edf5, #dce1e8);
    border-radius: 18px; padding: 10px 16px;
    box-shadow: 10px 10px 20px #8f95a0, -6px -6px 12px #ffffff;
    display: flex; align-items: center; justify-content: space-between;
    gap: 10px; flex-wrap: wrap;
  }
  .lvl-filter-label {
    display: flex; align-items: center; gap: 6px;
    font-size: 10px; font-weight: 800;
    background: linear-gradient(135deg, #2563eb, #8b5cf6);
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    text-transform: uppercase; letter-spacing: .1em;
  }
  .lvl-filter-label-icon {
    width: 30px; height: 30px; border-radius: 12px;
    background: linear-gradient(135deg, #1e3a5f, #2563eb);
    box-shadow: 5px 5px 10px #8f95a0, -3px -3px 8px #ffffff;
    display: flex; align-items: center; justify-content: center;
    color: #fff; flex-shrink: 0;
  }
  .lvl-filter-controls {
    display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
  }
  .lvl-filter-nav {
    display: flex; align-items: center; gap: 6px;
    background: linear-gradient(145deg, #e8edf5, #dce1e8);
    border-radius: 60px; padding: 4px 8px;
    box-shadow: inset 2px 2px 4px rgba(0,0,0,0.05), inset -2px -2px 4px rgba(255,255,255,0.7);
  }
  .lvl-filter-nav-btn {
    width: 30px; height: 30px; border-radius: 50%; border: none;
    background: linear-gradient(145deg, #e8edf5, #dce1e8);
    cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    color: #64748b;
    box-shadow: 4px 4px 8px #8f95a0, -2px -2px 6px #ffffff;
    transition: all 0.15s;
  }
  .lvl-filter-nav-btn:hover:not(:disabled) {
    background: linear-gradient(135deg, #1e3a5f, #2563eb);
    color: #fff;
    transform: scale(1.05);
  }
  .lvl-filter-nav-btn:disabled { opacity: 0.4; cursor: not-allowed; }
  .lvl-filter-month-display {
    font-size: 12px; font-weight: 800;
    background: linear-gradient(135deg, #1e293b, #334155);
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    min-width: 110px; text-align: center; padding: 0 4px;
  }
  .lvl-filter-select-wrap {
    display: flex; align-items: center; gap: 6px;
  }
  .lvl-filter-select {
    appearance: none; -webkit-appearance: none;
    background: linear-gradient(145deg, #e8edf5, #dce1e8);
    border: none;
    border-radius: 60px;
    padding: 6px 24px 6px 12px;
    font-size: 10px; font-weight: 700; color: #1e293b;
    font-family: 'Montserrat', sans-serif;
    cursor: pointer; outline: none;
    box-shadow: inset 2px 2px 5px rgba(0,0,0,0.08), inset -2px -2px 4px rgba(255,255,255,0.6);
    transition: all 0.2s;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%232563eb' stroke-width='2.5'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 10px center;
  }
  .lvl-filter-select:focus {
    box-shadow: 0 0 0 3px rgba(37,99,235,0.3), inset 3px 3px 6px rgba(0,0,0,0.08);
  }
  .lvl-filter-current-badge {
    display: inline-flex; align-items: center; gap: 4px;
    background: linear-gradient(135deg, rgba(251,191,36,0.15), rgba(245,158,11,0.08));
    border: 1px solid rgba(251,191,36,0.3);
    border-radius: 99px; padding: 4px 10px;
    font-size: 10px; font-weight: 800; color: #d97706;
    letter-spacing: .05em;
    backdrop-filter: blur(4px);
  }
  .lvl-filter-current-dot {
    width: 6px; height: 6px; border-radius: 50%; background: #fbbf24;
    box-shadow: 0 0 4px rgba(251,191,36,0.8);
    animation: lvl-pulse 2s infinite;
  }
  .lvl-filter-loading {
    display: flex; align-items: center; gap: 6px;
    font-size: 10px; font-weight: 700; color: #2563eb;
  }
  .lvl-filter-spinner {
    width: 14px; height: 14px; border-radius: 50%;
    border: 2px solid rgba(37,99,235,0.2);
    border-top-color: #2563eb;
    animation: lvl-spin .6s linear infinite;
  }
  @keyframes lvl-spin { to { transform: rotate(360deg); } }

  /* ══════════════════════════════
     SECTION LABEL
  ══════════════════════════════ */
  .lvl-section-label {
    font-size: 10px; font-weight: 800;
    background: linear-gradient(135deg, #2563eb, #8b5cf6);
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    text-transform: uppercase; letter-spacing: .12em;
    display: flex; align-items: center; gap: 10px;
  }
  .lvl-section-label::after {
    content: ''; flex: 1; height: 1.5px;
    background: linear-gradient(90deg, #2563eb, #8b5cf6, transparent);
    border-radius: 99px;
  }

  /* ══════════════════════════════
     TWO-COL GRID
  ══════════════════════════════ */
  .lvl-two-col {
    display: grid; grid-template-columns: 1fr 1fr; gap: 16px;
  }
  @media (max-width: 768px) { .lvl-two-col { grid-template-columns: 1fr; } }

  /* ══════════════════════════════
     SHARED CARD — Neumorphism
  ══════════════════════════════ */
  .lvl-card {
    background: linear-gradient(145deg, #e8edf5, #dce1e8);
    border-radius: 7px; padding: 16px;
    box-shadow: 10px 10px 18px #8f95a0, -5px -5px 12px #ffffff;
  }
  .lvl-card-title {
    font-size: 11px; font-weight: 900;
    background: linear-gradient(135deg, #2563eb, #8b5cf6);
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    text-transform: uppercase; letter-spacing: .09em; margin-bottom: 12px;
    display: flex; align-items: center; gap: 6px;
  }
  .lvl-card-title-bar {
    width: 4px; height: 16px; border-radius: 99px;
    background: linear-gradient(180deg, #fbbf24, #f59e0b);
    box-shadow: 0 2px 6px rgba(245,158,11,0.4);
  }

  /* Income mini cards */
  .lvl-income-grid {
    display: grid; grid-template-columns: 1fr 1fr; gap: 10px;
  }
  .lvl-income-mini {
    border-radius: 14px; padding: 10px;
    transition: all 0.2s;
    box-shadow: inset 2px 2px 5px rgba(0,0,0,0.05), inset -2px -2px 5px rgba(255,255,255,0.6);
  }
  .lvl-income-mini:hover { transform: translateY(-2px); }
  .lvl-income-mini.blue {
    background: linear-gradient(145deg, rgba(59,130,246,0.12), rgba(37,99,235,0.08));
    border: 1px solid rgba(59,130,246,0.25);
  }
  .lvl-income-mini.green {
    background: linear-gradient(145deg, rgba(16,185,129,0.12), rgba(5,150,105,0.08));
    border: 1px solid rgba(16,185,129,0.25);
  }
  .lvl-income-mini.purple {
    background: linear-gradient(145deg, rgba(139,92,246,0.12), rgba(124,58,237,0.08));
    border: 1px solid rgba(139,92,246,0.25);
  }
  .lvl-income-mini.amber {
    background: linear-gradient(145deg, rgba(245,158,11,0.12), rgba(217,119,6,0.08));
    border: 1px solid rgba(245,158,11,0.25);
  }
  .lvl-income-mini-label {
    font-size: 9px; font-weight: 800; text-transform: uppercase;
    letter-spacing: .08em; margin-bottom: 4px;
  }
  .lvl-income-mini.blue .lvl-income-mini-label { color: #3b82f6; }
  .lvl-income-mini.green .lvl-income-mini-label { color: #10b981; }
  .lvl-income-mini.purple .lvl-income-mini-label { color: #8b5cf6; }
  .lvl-income-mini.amber .lvl-income-mini-label { color: #f59e0b; }
  .lvl-income-mini-val {
    font-size: clamp(14px, 1.8vw, 18px); font-weight: 900; color: #1e293b;
  }

  /* ══════════════════════════════
     LEVEL SUMMARY CARDS — Bold Colors
  ══════════════════════════════ */
  .lvl-level-grid {
    display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px;
  }
  @media (max-width: 768px) { .lvl-level-grid { grid-template-columns: 1fr; } }

  .lvl-level-card {
    border-radius: 7px; padding: 14px;
    position: relative; overflow: hidden;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 10px 10px 18px #8f95a0, -5px -5px 12px #ffffff;
  }
  .lvl-level-card:hover {
    transform: translateY(-4px);
    box-shadow: 16px 16px 26px #8f95a0, -8px -8px 16px #ffffff;
  }
  .lvl-level-card.is-loading { opacity: 0.6; pointer-events: none; }

  .lvl-level-card.blue {
    background: linear-gradient(135deg, #1e3a5f, #2563eb);
    border: 1px solid rgba(59,130,246,0.3);
  }
  .lvl-level-card.green {
    background: linear-gradient(135deg, #064e3b, #10b981);
    border: 1px solid rgba(16,185,129,0.3);
  }
  .lvl-level-card.purple {
    background: linear-gradient(135deg, #4c1d95, #8b5cf6);
    border: 1px solid rgba(139,92,246,0.3);
  }

  .lvl-level-card::before {
    content: ''; position: absolute; top: -26px; right: -26px;
    width: 130px; height: 130px; border-radius: 50%;
    background: radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%);
  }
  .lvl-level-card::after {
    content: ''; position: absolute; bottom: -26px; left: -26px;
    width: 100px; height: 100px; border-radius: 50%;
    background: radial-gradient(circle, rgba(255,255,255,0.06) 0%, transparent 70%);
  }

  .lvl-level-top {
    display: flex; align-items: flex-start; justify-content: space-between;
    margin-bottom: 10px; position: relative; z-index: 1;
  }
  .lvl-level-tag {
    font-size: 9px; font-weight: 800; text-transform: uppercase;
    letter-spacing: .1em; opacity: 0.9; color: rgba(255,255,255,0.8);
  }
  .lvl-level-emoji { font-size: 20px; opacity: 0.8; }
  .lvl-level-count {
    font-size: clamp(24px, 3.5vw, 32px); font-weight: 900;
    color: #fff; line-height: 1; position: relative; z-index: 1;
    text-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }
  .lvl-level-count-label {
    font-size: 9px; font-weight: 600; color: rgba(255,255,255,0.7);
    letter-spacing: .05em;
  }
  .lvl-level-divider {
    height: 1.5px; background: linear-gradient(90deg, rgba(255,255,255,0.1), rgba(255,255,255,0.3), rgba(255,255,255,0.1));
    margin: 10px 0; border-radius: 99px;
  }
  .lvl-level-bottom { position: relative; z-index: 1; }
  .lvl-level-rate {
    font-size: 10px; font-weight: 700; color: rgba(255,255,255,0.7);
  }
  .lvl-level-income {
    font-size: clamp(16px, 2.2vw, 20px); font-weight: 900; color: #fbbf24;
    text-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }

  /* ══════════════════════════════
     BREAKDOWN TABLE — Overall Section
  ══════════════════════════════ */
  .lvl-breakdown-grid {
    display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 16px;
  }
  @media (max-width: 768px) { .lvl-breakdown-grid { grid-template-columns: 1fr; } }

  .lvl-breakdown-card {
    background: linear-gradient(145deg, #e8edf5, #dce1e8);
    border-radius: 7px; padding: 14px;
    box-shadow: 10px 10px 18px #8f95a0, -5px -5px 12px #ffffff;
    transition: all 0.2s;
  }
  .lvl-breakdown-card:hover { transform: translateY(-2px); box-shadow: 12px 12px 20px #8f95a0, -5px -5px 12px #ffffff; }

  .lvl-breakdown-header {
    display: flex; align-items: center; justify-content: space-between;
    margin-bottom: 10px;
    flex-wrap: wrap; gap: 8px;
  }
  .lvl-breakdown-title {
    font-size: 11px; font-weight: 900;
    background: linear-gradient(135deg, #2563eb, #8b5cf6);
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    text-transform: uppercase; letter-spacing: .08em;
  }
  .lvl-breakdown-rate {
    display: inline-flex; align-items: center; gap: 4px;
    padding: 4px 10px; border-radius: 99px;
    font-size: 10px; font-weight: 800;
    background: linear-gradient(145deg, #e8edf5, #dce1e8);
    box-shadow: inset 2px 2px 4px rgba(0,0,0,0.05), inset -2px -2px 4px rgba(255,255,255,0.6);
  }
  .lvl-breakdown-rate.blue { color: #3b82f6; }
  .lvl-breakdown-rate.green { color: #10b981; }
  .lvl-breakdown-rate.purple { color: #8b5cf6; }

  .lvl-breakdown-stats {
    display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px;
    margin-bottom: 12px;
  }
  .lvl-breakdown-stat {
    background: linear-gradient(145deg, #f0f4f9, #e8edf5);
    border-radius: 14px; padding: 10px;
    box-shadow: inset 2px 2px 5px rgba(0,0,0,0.05), inset -2px -2px 5px rgba(255,255,255,0.7);
  }
  .lvl-breakdown-stat-label {
    font-size: 8px; font-weight: 800; color: #8b5cf6;
    text-transform: uppercase; letter-spacing: .08em; margin-bottom: 4px;
  }
  .lvl-breakdown-stat-val {
    font-size: 14px; font-weight: 900; color: #ef4444;
  }
  .lvl-breakdown-stat-val2 {
    font-size: 14px; font-weight: 900; color: #10b981;
  }

  .lvl-breakdown-income {
    background: linear-gradient(145deg, #e8edf5, #dce1e8);
    border-radius: 14px; padding: 10px 12px;
    display: flex; align-items: center; justify-content: space-between;
    box-shadow: 6px 6px 12px rgba(0,0,0,0.08), -3px -3px 6px rgba(255,255,255,0.6);
  }
  .lvl-breakdown-income-label {
    font-size: 10px; font-weight: 800; color: #64748b;
    text-transform: uppercase; letter-spacing: .08em;
  }
  .lvl-breakdown-income-val {
    font-size: 16px; font-weight: 900;
  }
  .lvl-breakdown-income-val.blue { color: #3b82f6; }
  .lvl-breakdown-income-val.green { color: #10b981; }
  .lvl-breakdown-income-val.purple { color: #8b5cf6; }

  /* ── How It Works Section ── */
  .lvl-how-grid {
    display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px;
  }
  @media (max-width: 768px) { .lvl-how-grid { grid-template-columns: 1fr; } }

  .lvl-how-card {
    background: linear-gradient(145deg, #e8edf5, #dce1e8);
    border-radius: 7px; padding: 14px;
    box-shadow: 10px 10px 18px #8f95a0, -5px -5px 12px #ffffff;
    display: flex; flex-direction: column; gap: 10px;
    transition: all 0.2s;
  }
  .lvl-how-card:hover { transform: translateY(-2px); }
  .lvl-how-icon-wrap {
    width: 40px; height: 40px; border-radius: 14px;
    display: flex; align-items: center; justify-content: center; font-size: 18px;
    box-shadow: 6px 6px 12px #8f95a0, -3px -3px 6px #ffffff;
  }
  .lvl-how-icon-wrap.blue { background: linear-gradient(135deg, #2563eb, #3b82f6); color: #fff; }
  .lvl-how-icon-wrap.green { background: linear-gradient(135deg, #059669, #10b981); color: #fff; }
  .lvl-how-icon-wrap.purple { background: linear-gradient(135deg, #7c3aed, #8b5cf6); color: #fff; }
  .lvl-how-title {
    font-size: 12px; font-weight: 800; color: #1e293b;
  }
  .lvl-how-desc {
    font-size: 10px; font-weight: 600; color: #64748b; line-height: 1.5;
  }
  .lvl-how-rate {
    font-weight: 900; font-size: 12px;
  }
  .lvl-how-rate.blue { color: #3b82f6; }
  .lvl-how-rate.green { color: #10b981; }
  .lvl-how-rate.purple { color: #8b5cf6; }
  .lvl-how-step {
    display: inline-flex; align-items: center; justify-content: center;
    width: 20px; height: 20px; border-radius: 50%;
    font-size: 10px; font-weight: 900; color: #fff;
    background: linear-gradient(135deg, #1e3a5f, #2563eb);
    box-shadow: 0 3px 6px rgba(0,0,0,0.2);
  }

  /* No Data State */
  .lvl-no-data {
    background: linear-gradient(145deg, #f0f4f9, #e8edf5);
    border-radius: 18px; padding: 24px 18px;
    box-shadow: inset 5px 5px 12px rgba(0,0,0,0.08), inset -5px -5px 12px rgba(255,255,255,0.6);
    text-align: center;
  }
  .lvl-no-data-icon { font-size: 34px; margin-bottom: 10px; }
  .lvl-no-data-text {
    font-size: 13px; font-weight: 800;
    background: linear-gradient(135deg, #2563eb, #8b5cf6);
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    margin-bottom: 5px;
  }
  .lvl-no-data-sub { font-size: 11px; font-weight: 600; color: #94a3b8; }

  /* Skeleton Loader */
  .lvl-skeleton {
    display: flex; flex-direction: column; gap: 18px; padding: 16px;
    max-width: 1280px; margin: 0 auto;
  }
  .lvl-skel {
    border-radius: 24px;
    background: #dce1e8;
    position: relative;
    overflow: hidden;
    box-shadow: 8px 8px 16px #8f95a0, -8px -8px 16px #ffffff;
  }
  .lvl-skel::after {
    content: '';
    position: absolute; inset: 0;
    background: linear-gradient(90deg, transparent 25%, rgba(255,255,255,0.5) 50%, transparent 75%);
    background-size: 200% 100%;
    animation: lvl-shimmer 1.4s infinite;
  }
  @keyframes lvl-shimmer {
    from { background-position: -200% 0; }
    to { background-position: 200% 0; }
  }
  .lvl-skel-hero { height: 130px; }
  .lvl-skel-row { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
  @media (max-width: 768px) { .lvl-skel-row { grid-template-columns: 1fr; } }
  .lvl-skel-box { height: 200px; border-radius: 22px; background: #dce1e8; position: relative; overflow: hidden; }
  .lvl-skel-box::after {
    content: '';
    position: absolute; inset: 0;
    background: linear-gradient(90deg, transparent 25%, rgba(255,255,255,0.5) 50%, transparent 75%);
    background-size: 200% 100%;
    animation: lvl-shimmer 1.4s infinite;
  }
  .lvl-skel-card { height: 230px; }

  .lvl-error {
    display: flex; flex-direction: column; align-items: center;
    justify-content: center; padding: 60px 30px; text-align: center;
    gap: 16px;
    background: linear-gradient(135deg, #1e3a5f, #2563eb);
    border-radius: 32px;
    margin: 30px;
    box-shadow: 16px 16px 32px #8f95a0, -8px -8px 16px #ffffff;
  }
  .lvl-error-icon { font-size: 54px; }
  .lvl-error-text { font-size: 16px; font-weight: 700; color: #ffffff; }

  /* Modal Styles */
  .lvl-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 12px;
  }

  .lvl-modal {
    background: linear-gradient(135deg, #e0e5ec 0%, #d1d6df 100%);
    border-radius: 14px;
    box-shadow: 10px 10px 20px #8f95a0, -6px -6px 12px #ffffff;
    width: 100%;
    max-width: 500px;
    max-height: 90vh;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  .lvl-modal-header {
    padding: 14px 18px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid rgba(0,0,0,0.1);
  }

  .lvl-modal-title {
    font-size: 16px;
    font-weight: 800;
    background: linear-gradient(135deg, #2563eb, #8b5cf6);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .lvl-modal-close-btn {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: linear-gradient(145deg, #e8edf5, #d1d6df);
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 4px 4px 8px #8f95a0, -3px -3px 6px #ffffff;
    transition: all 0.2s;
  }

  .lvl-modal-close-btn:hover {
    transform: scale(1.05);
  }

  .lvl-modal-tabs {
    display: flex;
    padding: 10px 18px;
    gap: 10px;
    border-bottom: 1px solid rgba(0,0,0,0.1);
  }

  .lvl-modal-tab {
    padding: 6px 12px;
    border-radius: 10px;
    border: none;
    cursor: pointer;
    font-size: 12px;
    font-weight: 700;
    background: linear-gradient(145deg, #e8edf5, #d1d6df);
    color: #64748b;
    box-shadow: 4px 4px 8px #8f95a0, -3px -3px 6px #ffffff;
    transition: all 0.2s;
  }

  .lvl-modal-tab.active {
    background: linear-gradient(135deg, #2563eb, #8b5cf6);
    color: white;
  }

  .lvl-modal-content {
    padding: 16px 18px;
    overflow-y: auto;
    flex: 1;
  }

  .lvl-member-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .lvl-member-card {
    background: linear-gradient(145deg, #e8edf5, #d1d6df);
    border-radius: 10px;
    padding: 12px;
    box-shadow: inset 2px 2px 5px rgba(0,0,0,0.05), inset -2px -2px 5px rgba(255,255,255,0.7);
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .lvl-member-avatar {
    width: 38px;
    height: 38px;
    border-radius: 50%;
    background: linear-gradient(135deg, #2563eb, #8b5cf6);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 20px;
  }

  .lvl-member-info {
    flex: 1;
  }

  .lvl-member-name {
    font-size: 14px;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 3px;
  }

  .lvl-member-details {
    font-size: 11px;
    font-weight: 600;
    color: #64748b;
  }
`;

/* ═══════════════════════════════════════════
   CONSTANTS
═══════════════════════════════════════════ */
const MONTHS = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December"
];

const LEVELS = [
  { key: "level1", color: "blue",   emoji: "👤",           label: "Level 1 (Capital Support Associate)",    rate: "5%"  },
  { key: "level2", color: "green",  emoji: "👥",           label: "Level 2 (Capital Growth Associate)",     rate: "3%"  },
  { key: "level3", color: "purple", emoji: "👨‍👩‍👧‍👦", label: "Level 3 (Capital Leadership Associate)", rate: "2%"  },
];

const THIS_YEAR  = new Date().getFullYear();
const THIS_MONTH = new Date().getMonth();

const YEAR_OPTIONS = Array.from(
  { length: THIS_YEAR - 2022 },
  (_, i) => 2023 + i
);

const fmt = (n) => Number(n || 0).toLocaleString("en-IN");

/* ═══════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════ */
const LevelIncomeReport = () => {
  const [data,         setData]         = useState(null);
  const [overallData,  setOverallData]  = useState(null);
  const [initialLoad,  setInitialLoad]  = useState(true);
  const [filterLoading,setFilterLoading]= useState(false);
  const [error,        setError]        = useState(false);

  const [selectedMonth, setSelectedMonth] = useState(THIS_MONTH);
  const [selectedYear,  setSelectedYear]  = useState(THIS_YEAR);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [selectedDataType, setSelectedDataType] = useState("monthly"); // "monthly" or "overall"
  const [activeTab, setActiveTab] = useState("registered"); // "registered" or "active"

  const isCurrentMonth =
    selectedMonth === THIS_MONTH && selectedYear === THIS_YEAR;

  const fetchData = useCallback(async (month, year, isFirst = false) => {
    if (isFirst) setInitialLoad(true); else setFilterLoading(true);
    setError(false);
    try {
      const res = await axios.get("/share/team-data", {
        params: { month: month + 1, year },
      });
      setData(res.data);
    } catch (err) {
      console.error("Error fetching team data:", err);
      setError(true);
    } finally {
      setInitialLoad(false);
      setFilterLoading(false);
    }
  }, []);

  const fetchOverallData = useCallback(async () => {
    try {
      const res = await axios.get("/share/team-data", {
        params: { overall: true }
      });
      setOverallData(res.data);
    } catch (err) {
      console.error("Error fetching overall team data:", err);
    }
  }, []);

  useEffect(() => {
    fetchData(selectedMonth, selectedYear, true);
    fetchOverallData();
  }, []);

  const isFirstRender = useState(true);
  useEffect(() => {
    if (isFirstRender[0]) { isFirstRender[1](false); return; }
    fetchData(selectedMonth, selectedYear, false);
  }, [selectedMonth, selectedYear]);

  const goPrev = () => {
    if (selectedMonth === 0) {
      setSelectedMonth(11);
      setSelectedYear(y => y - 1);
    } else {
      setSelectedMonth(m => m - 1);
    }
  };
  const goNext = () => {
    if (selectedMonth === 11) {
      setSelectedMonth(0);
      setSelectedYear(y => y + 1);
    } else {
      setSelectedMonth(m => m + 1);
    }
  };

  const canGoNext =
    selectedYear < THIS_YEAR ||
    (selectedYear === THIS_YEAR && selectedMonth < THIS_MONTH);
  const canGoPrev = selectedYear > 2023 || selectedMonth > 0;

  const openModal = (levelKey, dataType = "monthly") => {
    setSelectedLevel(levelKey);
    setSelectedDataType(dataType);
    setActiveTab("registered");
    setModalOpen(true);
  };

  if (initialLoad) return (
    <div className="lvl-root">
      <style>{styles}</style>
      <div className="lvl-skeleton">
        <div className="lvl-skel lvl-skel-hero" />
        <div className="lvl-skel lvl-skel-hero" style={{ height: 80 }} />
        <div className="lvl-skel-row">
          {[1,2,3].map(i => <div className="lvl-skel-box" key={i}/>)}
        </div>
        <div className="lvl-skel lvl-skel-card" />
        <div className="lvl-skel lvl-skel-card" />
      </div>
    </div>
  );

  if (error || !data) return (
    <div className="lvl-root">
      <style>{styles}</style>
      <div className="lvl-error">
        <div className="lvl-error-icon">😕</div>
        <div className="lvl-error-text">Failed to load report</div>
      </div>
    </div>
  );

  const totalMembers       = data.totals?.teamMembers;
  const totalDirectMembers = data.totals?.directActiveCount;
  const hasActivity        = data.isActive;

  return (
    <div className="lvl-root">
      <style>{styles}</style>
      <div className="lvl-page">

        {/* ── Hero Section ── */}
        <div className="lvl-hero">
          <div className="lvl-hero-left">
            <div className="lvl-hero-badge">
              <div className="lvl-badge-dot"/>
              <FiAward size={14} /> CCP Income History
            </div>
            <div className="lvl-hero-title">Capital Connector Partner</div>
            <div className="lvl-hero-sub">Multi-level commission breakdown & analytics</div>
          </div>
          <div className="lvl-hero-stats">
            <div className="lvl-hero-stat">
              <div className="lvl-hero-stat-val">{totalDirectMembers ?? "—"}</div>
              <div className="lvl-hero-stat-label">Active Direct Members</div>
            </div>
            <div className="lvl-hero-stat">
              <div className="lvl-hero-stat-val">{totalMembers ?? "—"}</div>
              <div className="lvl-hero-stat-label">Total Active Members</div>
            </div>
          </div>
        </div>

        {/* ── Month Filter Bar ── */}
        <div className="lvl-filter-bar">
          <div className="lvl-filter-label">
            <div className="lvl-filter-label-icon"><FiCalendar size={18}/></div>
            Viewing Period
          </div>

          <div className="lvl-filter-controls">
            <div className="lvl-filter-nav">
              <button
                className="lvl-filter-nav-btn"
                onClick={goPrev}
                disabled={!canGoPrev || filterLoading}
              >
                <FiChevronLeft size={18}/>
              </button>
              <div className="lvl-filter-month-display">
                {MONTHS[selectedMonth]} {selectedYear}
              </div>
              <button
                className="lvl-filter-nav-btn"
                onClick={goNext}
                disabled={!canGoNext || filterLoading}
              >
                <FiChevronRight size={18}/>
              </button>
            </div>

            <div className="lvl-filter-select-wrap">
              <select
                className="lvl-filter-select"
                value={selectedMonth}
                onChange={e => setSelectedMonth(Number(e.target.value))}
                disabled={filterLoading}
              >
                {MONTHS.map((m, i) => (
                  <option
                    key={m}
                    value={i}
                    disabled={selectedYear === THIS_YEAR && i > THIS_MONTH}
                  >
                    {m}
                  </option>
                ))}
              </select>
              <select
                className="lvl-filter-select"
                value={selectedYear}
                onChange={e => setSelectedYear(Number(e.target.value))}
                disabled={filterLoading}
              >
                {YEAR_OPTIONS.map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>

            {filterLoading ? (
              <div className="lvl-filter-loading">
                <div className="lvl-filter-spinner"/>
                Loading data...
              </div>
            ) : isCurrentMonth ? (
              <div className="lvl-filter-current-badge">
                <div className="lvl-filter-current-dot"/>
                Current Month
              </div>
            ) : null}
          </div>
        </div>

        {/* ── Overview Section ── */}
        <div className="lvl-section-label">
          <FiPieChart size={14} /> Overview
        </div>
        <div className="lvl-two-col">
          <div className="lvl-card">
            <div className="lvl-card-title">
              <div className="lvl-card-title-bar"/>
              Your Actual Income
            </div>
            {hasActivity ? (
              <div className="lvl-income-grid">
                <MiniIncome variant="blue"   label="CCP Income"        val={data.currentUserIncome?.levelIncome      || 0} />
                <MiniIncome variant="green"  label="EMI Income"        val={data.currentUserIncome?.roiIncome        || 0} />
                <MiniIncome variant="purple" label="Royalty Income"    val={data.currentUserIncome?.royaltyIncome    || 0} />
                <MiniIncome variant="amber"  label="Total Loan Income" val={data.currentUserIncome?.totalShareIncome || 0} />
              </div>
            ) : (
              <div className="lvl-no-data">
                <div className="lvl-no-data-icon">💼</div>
                <div className="lvl-no-data-text">No active investment</div>
                <div className="lvl-no-data-sub">Income data is available for active members only</div>
              </div>
            )}
          </div>
        </div>

        {/* ── Level Summary ── */}
        <div className="lvl-section-label">
          <FiBarChart2 size={14} /> Level Summary — {MONTHS[selectedMonth]} {selectedYear}
        </div>

        {hasActivity ? (
          <div className="lvl-level-grid">
            {LEVELS.map(({ key, color, emoji, label, rate }) => (
              <div 
                key={key} 
                className={`lvl-level-card ${color}${filterLoading ? " is-loading" : ""}`}
                style={{ cursor: 'pointer' }}
                onClick={() => openModal(key)}
              >
                <div className="lvl-level-top">
                  <div className="lvl-level-tag">{label}</div>
                  <div className="lvl-level-emoji">{emoji}</div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div className="lvl-level-count">{data[key]?.count || 0}</div>
                  <div className="lvl-level-count-label">Registered Members</div>
                </div>
                <div className="lvl-level-divider" />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div className="lvl-level-count">{data[key]?.activeMember || 0}</div>
                  <div className="lvl-level-count-label">Active Members</div>
                </div>
                <div className="lvl-level-divider" />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div className="lvl-level-count">₹{fmt(data[key]?.totalInvestment)}</div>
                  <div className="lvl-level-count-label">Total Loan Pay</div>
                </div>
                <div className="lvl-level-divider" />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }} className="lvl-level-bottom">
                  <div className="lvl-level-income">₹{fmt(data[key]?.income)}</div>
                  <div className="lvl-level-rate">Commission Rate: {rate}</div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="lvl-no-data">
            <div className="lvl-no-data-icon">📊</div>
            <div className="lvl-no-data-text">No level data for {MONTHS[selectedMonth]} {selectedYear}</div>
            <div className="lvl-no-data-sub">Activate your account to view level summaries</div>
          </div>
        )}

        {/* ── Overall Breakdown (All-time) ── */}
        {hasActivity && (
          <>
            <div className="lvl-section-label">
              <FiGlobe size={14} /> All Level Summary — Overall (Lifetime)
            </div>
            <div className="lvl-breakdown-grid">
              {LEVELS.map(({ key, color, label, rate }) => (
                <div 
                  key={key} 
                  className={`lvl-breakdown-card`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => openModal(key, "overall")}
                >
                  <div className="lvl-breakdown-header">
                    <div className="lvl-breakdown-title">{label}</div>
                    <div className={`lvl-breakdown-rate ${color}`}>{rate}</div>
                  </div>
                  <div className="lvl-breakdown-stats">
                    <div className="lvl-breakdown-stat">
                      <div className="lvl-breakdown-stat-label">Registered Members</div>
                      <div className="lvl-breakdown-stat-val">{overallData?.[key]?.count || 0}</div>
                    </div>
                    <div className="lvl-breakdown-stat">
                      <div className="lvl-breakdown-stat-label">Active Members</div>
                      <div className="lvl-breakdown-stat-val2">{overallData?.[key]?.activeMember || 0}</div>
                    </div>
                    <div className="lvl-breakdown-stat">
                      <div className="lvl-breakdown-stat-label">Total Loan Pay</div>
                      <div className="lvl-breakdown-stat-val2">₹{fmt(overallData?.[key]?.totalInvestment)}</div>
                    </div>
                  </div>
                  <div className="lvl-breakdown-income">
                    <div className="lvl-breakdown-income-label">Total Income Earned</div>
                    <div className={`lvl-breakdown-income-val ${color}`}>₹{fmt(overallData?.[key]?.income)}</div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* ── How It Works Section ── */}
        <div className="lvl-section-label">
          <FiTrendingUp size={14} /> How It Works
        </div>
        <div className="lvl-how-grid">
          {[
            { color:"blue",   icon:<FiUsers size={22}/>,     step:1, title:"Direct Members (Level 1)",    desc:"Earn", rate:"5%", after:"of each direct referral's total loan pay" },
            { color:"green",  icon:<FiTrendingUp size={22}/>, step:2, title:"Secondary Members (Level 2)", desc:"Earn", rate:"3%", after:"of level 2 member's total loan pay" },
            { color:"purple", icon:<FiDollarSign size={22}/>, step:3, title:"Tertiary Members (Level 3)",  desc:"Earn", rate:"2%", after:"of level 3 member's total loan pay" },
          ].map(({ color, icon, step, title, desc, rate, after }) => (
            <div key={step} className="lvl-how-card">
              <div style={{ display:"flex", alignItems:"center", gap: 12 }}>
                <div className={`lvl-how-icon-wrap ${color}`}>{icon}</div>
                <span className="lvl-how-step">{step}</span>
              </div>
              <div className="lvl-how-title">{title}</div>
              <div className="lvl-how-desc">
                {desc} <span className={`lvl-how-rate ${color}`}>{rate}</span> {after}
              </div>
            </div>
          ))}
        </div>

      </div>

      {/* Modal Component */}
      {modalOpen && selectedLevel && (
        <div className="lvl-modal-overlay" onClick={() => setModalOpen(false)}>
          <div className="lvl-modal" onClick={(e) => e.stopPropagation()}>
            <div className="lvl-modal-header">
              <div className="lvl-modal-title">
                {LEVELS.find(l => l.key === selectedLevel)?.label} — {(selectedDataType === "monthly" ? data : overallData)?.period.label}
              </div>
              <button 
                className="lvl-modal-close-btn" 
                onClick={() => setModalOpen(false)}
              >
                <FiX size={20} />
              </button>
            </div>
            <div className="lvl-modal-tabs">
              <button 
                className={`lvl-modal-tab ${activeTab === "registered" ? "active" : ""}`}
                onClick={() => setActiveTab("registered")}
              >
                Registered ({(selectedDataType === "monthly" ? data : overallData)?.[selectedLevel]?.registeredMembers?.length || 0})
              </button>
              <button 
                className={`lvl-modal-tab ${activeTab === "active" ? "active" : ""}`}
                onClick={() => setActiveTab("active")}
              >
                Active ({(selectedDataType === "monthly" ? data : overallData)?.[selectedLevel]?.monthlyActiveMembers?.length || 0})
              </button>
            </div>
            <div className="lvl-modal-content">
              {activeTab === "registered" ? (
                <div className="lvl-member-list">
                  {(selectedDataType === "monthly" ? data : overallData)?.[selectedLevel]?.registeredMembers?.length > 0 ? (
                    (selectedDataType === "monthly" ? data : overallData)[selectedLevel].registeredMembers.map((member, idx) => (
                      <div key={idx} className="lvl-member-card">
                        <div className="lvl-member-avatar">
                          <FiUser />
                        </div>
                        <div className="lvl-member-info">
                          <div className="lvl-member-name">{member.name}</div>
                          <div className="lvl-member-details">
                            {member.uniqueId} • Joined {new Date(member.createdAt).toLocaleDateString()} {new Date(member.createdAt).toLocaleTimeString()}
                          </div>
                          {member.sponsorName && (
                            <div className="lvl-member-details" style={{ fontSize: '12px', marginTop: '4px' }}>
                              Sponsor: {member.sponsorName} ({member.sponsorUniqueId || member.sponsorId})
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="lvl-no-data">
                      <div className="lvl-no-data-icon">👤</div>
                      <div className="lvl-no-data-text">No registered members</div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="lvl-member-list">
                  {(selectedDataType === "monthly" ? data : overallData)?.[selectedLevel]?.monthlyActiveMembers?.length > 0 ? (
                    (selectedDataType === "monthly" ? data : overallData)[selectedLevel].monthlyActiveMembers.map((member, idx) => (
                      <div key={idx} className="lvl-member-card">
                        <div className="lvl-member-avatar">
                          <FiUser />
                        </div>
                        <div className="lvl-member-info">
                          <div className="lvl-member-name">{member.name}</div>
                          <div className="lvl-member-details">
                            {member.uniqueId} • Joined {new Date(member.createdAt).toLocaleDateString()} {new Date(member.createdAt).toLocaleTimeString()} • {member.isActive ? <><FiCheckCircle style={{ display: 'inline', marginRight: '4px' }} /> Active</> : 'Inactive'}
                          </div>
                          {member.sponsorName && (
                            <div className="lvl-member-details" style={{ fontSize: '12px', marginTop: '4px' }}>
                              Sponsor: {member.sponsorName} ({member.sponsorUniqueId || member.sponsorId})
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="lvl-no-data">
                      <div className="lvl-no-data-icon">📊</div>
                      <div className="lvl-no-data-text">No active members</div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const MiniIncome = ({ variant, label, val }) => (
  <div className={`lvl-income-mini ${variant}`}>
    <div className="lvl-income-mini-label">{label}</div>
    <div className="lvl-income-mini-val">₹{Number(val).toLocaleString("en-IN")}</div>
  </div>
);

export default LevelIncomeReport;